import { useCallback, useMemo } from 'react';
import type { Department, User } from '@bliks-io/contracts';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { useDepartments } from '@/api/queries/useDepartments';
import { useOrgFunctions } from '@/api/queries/useOrgFunctions';
import { departmentIdToWire } from '@/lib/department-ref';
import { normalizedKey } from '@/lib/draft-rows/normalizedKey';
import {
  createOwnerAwareProcessRowValidator,
  createOwnerAwareSystemRowValidator,
  createUnrepresentedMemberCheck,
  createUnseenRoleDescriptionCheck,
  userCrossRowChecks as baseUserCrossRowChecks
} from './utils/draftRowValidation';
import type { CrossRowCheck } from '@/lib/draft-rows/markDuplicateRows';
import {
  createLeadAwareOrgFunctionRowValidator,
  createMemberAwareMembershipRowValidator
} from './utils/orgFunctionRowValidation';
import type { ParsedUser } from './utils/excelParsers';
import type { UserInput } from '@/lib/excel';

export interface WizardValidationContext {
  orgMembers: User[] | undefined;
  /** Gate: without the member lists the protected-row partition and the
   * unrepresented-member check silently no-op. */
  membersLoaded: boolean;
  departmentsList: Department[];
  emailToUser: Map<string, User>;
  /** Submit boundary: map the draft's stable department id back to the CURRENT
   * abbreviation so a rename is reflected on the wire (see departmentRef). */
  toWireDepartment: <TRow extends { department: string }>(row: TRow) => TRow;
  validateOrgFunctionRow: ReturnType<typeof createLeadAwareOrgFunctionRowValidator>;
  validateMembershipRow: ReturnType<typeof createMemberAwareMembershipRowValidator>;
  validateSystemRow: ReturnType<typeof createOwnerAwareSystemRowValidator>;
  validateProcessRow: ReturnType<typeof createOwnerAwareProcessRowValidator>;
  /** The users step's full cross-row composition (conflicting Stelle
   * descriptions, unrepresented-member collisions, unseen-role-description
   * overwrites) — assembled once so the loud and quiet passes can never
   * diverge. */
  userCrossRowChecks: CrossRowCheck<UserInput>[];
}

/**
 * The queries and validator composition BOTH validation passes consume — the
 * authoritative Weiter submit and the silent autosave flush. Single source on
 * purpose: the design invariant is that autosave never commits a row Weiter
 * would reject, which only holds while the two passes stay identical.
 */
export function useWizardValidationContext(userSeeds: ParsedUser[]): WizardValidationContext {
  const { data: orgMembers, isSuccess: internalMembersLoaded } = useOrgMembers({
    status: ['inactive', 'pending', 'active'],
    classification: 'internal'
  });
  // Unfiltered on purpose: the table can only represent the internal in-scope
  // members above, but a draft-row email may collide with ANYONE in the org —
  // the unrepresented-member check needs the full list to see the invisible
  // rest (externals, suspended internals).
  const { data: allOrgMembers, isSuccess: allMembersLoaded } = useOrgMembers();
  // Same query the Funktionen/Teams surface steps run for their inline
  // validators: internal members of EVERY status. Externals stay excluded by
  // design (they never hold OrgFunctions), but a suspended member still
  // leads/holds — a validator stricter than that would block seeded rows the
  // backend accepts.
  const { data: internalMembers } = useOrgMembers({ classification: 'internal' });
  const { data: departmentsList } = useDepartments();
  const { data: roleOrgFunctions } = useOrgFunctions('role');

  const emailToUser = useMemo(() => {
    const map = new Map<string, User>();
    orgMembers?.forEach((user) => map.set(user.email.toLowerCase(), user));
    return map;
  }, [orgMembers]);

  const toWireDepartment = useCallback(
    <TRow extends { department: string }>(row: TRow): TRow => ({
      ...row,
      department: departmentIdToWire(row.department, departmentsList ?? [])
    }),
    [departmentsList]
  );

  // Same member-aware validators the surface steps use inline, so both passes
  // flag unknown Leitung/membership emails identically.
  const internalMemberEmails = useMemo(
    () => new Set((internalMembers ?? []).map((member) => member.email.toLowerCase())),
    [internalMembers]
  );
  // Owner emails accept exactly what the backend resolves them against: ANY
  // user, regardless of status or classification. A stricter set would mark
  // seeded rows invalid (blocking Weiter and silencing autosave) even though
  // re-submitting them verbatim succeeds server-side.
  const resolvableEmails = useMemo(
    () => new Set((allOrgMembers ?? []).map((member) => member.email.toLowerCase())),
    [allOrgMembers]
  );
  const validateOrgFunctionRow = useMemo(
    () => createLeadAwareOrgFunctionRowValidator(internalMemberEmails),
    [internalMemberEmails]
  );
  const validateMembershipRow = useMemo(
    () => createMemberAwareMembershipRowValidator(internalMemberEmails),
    [internalMemberEmails]
  );
  const validateSystemRow = useMemo(
    () => createOwnerAwareSystemRowValidator(resolvableEmails),
    [resolvableEmails]
  );
  const validateProcessRow = useMemo(
    () => createOwnerAwareProcessRowValidator(resolvableEmails),
    [resolvableEmails]
  );

  // Descriptions of backend Stellen no seeded row surfaces — a new draft row
  // naming one with a different Beschreibung would silently overwrite it
  // server-side. Seeded roles are exempt: editing a visible description is the
  // designed update path.
  const unseenRoleDescriptions = useMemo(() => {
    const seededRoles = new Set(userSeeds.map((row) => normalizedKey(row.data.role)));
    const map = new Map<string, string>();
    for (const orgFunction of roleOrgFunctions ?? []) {
      const role = normalizedKey(orgFunction.name);
      const description = orgFunction.description?.trim();
      if (role && description && !seededRoles.has(role)) map.set(role, description);
    }
    return map;
  }, [roleOrgFunctions, userSeeds]);

  // Every representable member — replaceable seed or protected locked-identity
  // row — renders in the draft table, so the plain duplicate-email pass flags
  // those collisions; only members the table cannot show need their own check.
  const unrepresentedMemberEmails = useMemo(() => {
    const representableIds = new Set((orgMembers ?? []).map((member) => member._id));
    return new Set(
      (allOrgMembers ?? [])
        .filter((member) => !representableIds.has(member._id))
        .map((member) => normalizedKey(member.email))
    );
  }, [allOrgMembers, orgMembers]);

  const userCrossRowChecks = useMemo(
    () => [
      ...baseUserCrossRowChecks,
      createUnrepresentedMemberCheck(unrepresentedMemberEmails),
      createUnseenRoleDescriptionCheck(unseenRoleDescriptions)
    ],
    [unrepresentedMemberEmails, unseenRoleDescriptions]
  );

  return {
    orgMembers,
    membersLoaded: internalMembersLoaded && allMembersLoaded,
    departmentsList: departmentsList ?? [],
    emailToUser,
    toWireDepartment,
    validateOrgFunctionRow,
    validateMembershipRow,
    validateSystemRow,
    validateProcessRow,
    userCrossRowChecks
  };
}
